import { ColorStyle } from './../../models/color.style';
import { IAppState, IFrontendSettings } from './../../ngrx.stores/frontend.settings/states';
import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  HostBinding,
  inject,
} from '@angular/core';
import { RouterOutlet, ActivatedRoute, Router } from '@angular/router';
import { Observable, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/common/components/base.component';
import { isInAppWebView } from 'src/app/common/utils/browser.check';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import { select, Store } from '@ngrx/store';
import {
  frontendSettingsErrorSelector,
  frontendSettingsIsLoadingSelector,
  frontendSettingsSelector,
} from '../../ngrx.stores/frontend.settings/selectors';
import * as FrontendSettingsActions from '../../ngrx.stores/frontend.settings/actions';
import { routeConfigs } from 'src/app/common/constants/route.config';
import { BodyRendererService } from 'src/app/common/services/body.renderer/body.renderer.service';
import { AiBuddyPlaceListingComponent } from 'src/app/modules/place-calendar/components/ai.buddy.place.listing/ai.buddy.place.listing.component';

@Component({
  selector: 'ig-visitor',
  templateUrl: './visitor.component.html',
  styleUrls: ['./visitor.component.scss'],
  host: {
    style: 'font-family: Roboto, sans-serif',
  },
  encapsulation: ViewEncapsulation.None,
})
export class VisitorComponent extends BaseComponent implements OnInit {
  @HostBinding('class') hostClass = 'h-full block';
  public isSettingsLoaded = true;
  public isShowUpgradeBrowser = true;
  public isMobile = false;
  public isSidebarCollapsed = false;

  public isPlaceDetails = false;
  public isEmbeddingSubmitForm = false;
  public isJobBoard = false;
  public isWebView = false;

  isLoading$: Observable<boolean>;
  error$: Observable<string | null>;
  frontendSettings$: Observable<IFrontendSettings | null>;

  @ViewChild('outlet', { static: false }) private _outlet: RouterOutlet;

  isOneRowMode = false;

  renderService = inject(BodyRendererService);
  private cdRef = inject(ChangeDetectorRef);

  constructor(
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public mediaQueryService: MediaQueryService,

    private _store: Store<IAppState>
  ) {
    super({ activatedRoute });

    this.isWebView = isInAppWebView();

    this.mediaQueryService.isMobile$.pipe(takeUntil(this.isDestroyed)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.isLoading$ = this._store.pipe(select(frontendSettingsIsLoadingSelector));
    this.error$ = this._store.pipe(select(frontendSettingsErrorSelector));
    this.frontendSettings$ = this._store.pipe(select(frontendSettingsSelector));

    const clientNameCls = this.getClientName();
    if (clientNameCls) {
      this.hostClass += ' ig-' + clientNameCls;
    }
  }

  ngOnInit(): void {
    this._store.dispatch(FrontendSettingsActions.getFrontendSettings({ hubName: this.hubName }));

    const delay = this.getValidatedDelay(this.getScriptValue('delay'));
    this.isForPlaceDetailsEmbedding() && (this.isPlaceDetails = true);
    this.isForEmbeddingSubmitForm() && (this.isEmbeddingSubmitForm = true);
    this.isForJobBoard() && (this.isJobBoard = true);

    this.frontendSettings$.subscribe((settings: IFrontendSettings) => {
      if (settings) {
        this.setCustomerColor(settings.customSettings.Style);
        setTimeout(() => {
          if (this.isShowTravelBuddy() && (this.isAiAssistant() || !this.isWebView)) {
            if (settings.ai.travelBuddy.version === 'new') {
              this.renderService.appendToBody(AiBuddyPlaceListingComponent);
            }
          }
        }, delay);
        this.cdRef.detectChanges();
      }
    });
  }

  // https://itidigital.atlassian.net/browse/DXP-2693
  // client wants to embed place details into their page (not a popup window),
  // so we need to hide the sidebar/topbar
  isForPlaceDetailsEmbedding() {
    return this.router.url.includes(
      routeConfigs.visitor.placeCalendar.placeDetailsForEmbedding.path.split('/:')[0]
    );
  }

  isForEmbeddingSubmitForm() {
    console.log('this.router.url', this.router.url);
    return this.router.url.includes(routeConfigs.visitor.eventCalendar.submitFormForEmbedding.path);
  }

  isForJobBoard() {
    console.log('this.router.url', this.router.url);
    return this.router.url.includes(routeConfigs.visitor.jobBoard.path);
  }

  isShowTravelBuddy() {
    return (
      // !this.isOneRow() &&
      !this.isPlaceDetails &&
      !this.isEmbeddingSubmitForm &&
      !this.isJobBoard &&
      this.frontendSettings?.status === 'live' &&
      this.frontendSettings?.ai?.travelBuddy?.isActive &&
      (this.isAiAssistant() ||
        this.getScriptValue('travel-buddy') === 'true' ||
        this.frontendSettings.ai.travelBuddy.showOnAllClientWidgets ||
        !this.isScriptSolution)
    );
  }

  isShowMenuBar() {
    return (
      !this.isOneRow() && !this.isPlaceDetails && !this.isEmbeddingSubmitForm && !this.isJobBoard
    );
  }

  public isTopbar(settings: IFrontendSettings) {
    return !settings.customSettings.menuStyle.isSideBar && !this.isMobile && this.isNeedSideBar;
  }

  public isSidebar(settings: IFrontendSettings) {
    return settings.customSettings.menuStyle.isSideBar && !this.isMobile && this.isNeedSideBar;
  }

  // this is for the one row scrolling widget
  public isOneRow() {
    // return this._outlet?.activatedRoute.component === OneRowWidgetComponent;

    // setTimeout(() => {
    //   this.isOneRowMode =
    //     this._outlet?.activatedRoute.component === OneRowWidgetComponent;
    //   console.log('this.isOneRowMode', this.isOneRowMode);
    // });

    return this.router.url.includes(routeConfigs.visitor.eventCalendar.oneRow.path);
  }

  /** there are 2 types of embedding TravelBuddy
   * 1. only embed TravelBuddy to any client webpage
   * 2. embed both TravelBuddy and normal events/places calendar
   * here is for the first type, check if the embed URL is for embed TravelBuddy, then only load it without the calendar
   */
  public isAiAssistant() {
    if (this.router.url.includes(routeConfigs.visitor.ai.travelBuddy.path)) {
      return true;
    }
    return false;
  }

  private getValidatedDelay(value: string | number | null | undefined): number {
    const defaultDelay = 3000;

    if (typeof value === 'number') {
      return Number.isFinite(value) && value >= 0 ? value : defaultDelay;
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return defaultDelay;
      }

      const parsedValue = Number(trimmedValue);
      return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : defaultDelay;
    }

    return defaultDelay;
  }

  private setCustomerColor(data: ColorStyle) {
    if (!data) {
      return;
    }
    let head = document.getElementsByTagName('head')[0];
    let s = document.createElement('style');
    s.setAttribute('type', 'text/css');
    let css = '';
    if (data.defaultButton.backgroundColor) {
      css += `--default-btn-background-color: ${data.defaultButton.backgroundColor};`;
    }
    if (data.eventButton.backgroundColor) {
      css += `--event-btn-background-color: ${data.eventButton.backgroundColor};`;
    }
    if (data.placeButton.backgroundColor) {
      css += `--place-btn-background-color: ${data.placeButton.backgroundColor};`;
    }
    if (data.pinnedEventBorder.borderColor) {
      css += `--pinned-event-border-color: ${data.pinnedEventBorder.borderColor};`;
    }
    if (data.detailButton.backgroundColor) {
      css += `--detail-btn-background-color: ${data.detailButton.backgroundColor};`;
    }
    if (data.sidebarUnderline.color) {
      css += `--sidebar-underline-color: ${data.sidebarUnderline.color};`;
    }
    s.appendChild(
      document.createTextNode(`
            :root {
              ${css}
            }
        `)
    );
    head.appendChild(s);
  }

  public get isNeedSideBar() {
    try {
      /**
       * VisitorPlaceMapComponent needs to have topbar (in topbar mode) is because the multi-level nested dropdown menu would have UX issue
       * after view changed. So keep the topbar alive but hide it when switching to place map view
       * */
      // return this._outlet.activatedRoute.component === VisitorCalendarComponent ||
      //     //this._outlet.activatedRoute.component === VisitorEventMapComponent ||
      //     (!this.allFrontendSettings.customSettings.menuStyle.isSideBar && this._outlet.activatedRoute.component === VisitorPlaceMapComponent) ||
      //     this._outlet.activatedRoute.component === VisitorCreateEventComponent ||
      //     this._outlet.activatedRoute.component === VisitorCreatePlaceComponent;
      return true;
    } catch (e) {
      return true;
    }

    // don't know why using the Component Name VisitorTripComponent causes a script error
    //this._outlet.activatedRoute.component.toString().indexOf('VisitorTripComponent') !== -1 ;
  }

  public get isNoTopMenuForMobile() {
    return this._outlet.activatedRoute.component.toString().indexOf('VisitorTripComponent') !== -1;
  }

  public toggleSidebarCollapse() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  public upgradeBrowser() {
    window.open(
      'https://browser-update.org/update-browser.html?force_outdated=true#3.3.15:browser-update.org',
      '_blank'
    );
  }

  public hideUpgradeBrowser() {
    this.isShowUpgradeBrowser = false;
  }
}
